<template>
  <div class="history-page" :class="{ embedded: embedded }">
    <div class="container">
      <div class="page-header">
        <h2 v-if="!$props.embedded" class="page-title">观看历史</h2>
        <button
          v-if="list.length > 0"
          class="clear-btn"
          @click="confirmClear"
        >清空历史</button>
      </div>

      <!-- 加载状态 -->
      <div v-if="loading" class="loading">
        <div class="loading-spinner"></div>
        <span>加载中...</span>
      </div>

      <template v-else>
        <!-- 空状态 -->
        <div v-if="list.length === 0" class="empty-tip">
          <div class="empty-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12 6 12 12 16 14"></polyline>
            </svg>
          </div>
          <p class="empty-text">暂无观看记录</p>
          <p class="empty-sub">去发现更多精彩视频吧</p>
        </div>

        <!-- 列表 -->
        <div v-else class="history-list">
          <div
            v-for="item in list"
            :key="item.id"
            class="history-card"
            @click="goVideo(item.video_id || item.id)"
          >
            <div class="history-thumb">
              <img
                :src="item.cover || item.video_cover || defaultCover"
                alt="封面"
              >
              <span class="history-duration">{{ item.duration || item.video_duration || '' }}</span>
              <div class="thumb-overlay">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="white" stroke="none">
                  <polygon points="8,5 19,12 8,19"></polygon>
                </svg>
              </div>
            </div>
            <div class="history-info">
              <h4 class="history-title">{{ item.title || item.video_title }}</h4>
              <p class="history-meta">
                <span class="watch-time">观看了 {{ item.watch_duration || item.watch_time || '--' }}</span>
                <span class="history-date">{{ formatTime(item.createtime || item.watch_at) }}</span>
              </p>
            </div>
            <button
              class="delete-btn"
              @click.stop="deleteItem(item.id)"
              title="删除"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              </svg>
            </button>
          </div>
        </div>

        <!-- 分页 -->
        <div v-if="hasMore" class="load-more" @click="loadMore">
          <span>加载更多</span>
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </div>
        <div v-else-if="list.length > 0" class="load-end">
          <span class="load-end-line"></span>
          <span class="load-end-text">没有更多了</span>
          <span class="load-end-line"></span>
        </div>
      </template>
    </div>
    <PageFooter v-if="!$props.embedded" />
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import PageFooter from './Footer.vue';
import request from '../api/request';
import { getWatchHistoryList, clearWatchHistory } from '../api/watchHistory';

export default {
  name: 'WatchHistory',
  components: { PageFooter },
  props: {
    embedded: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    void props.embedded;
    const list = ref([]);
    const loading = ref(false);
    const page = ref(1);
    const hasMore = ref(true);
    const defaultCover = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="160" height="90" viewBox="0 0 16 9" fill="%23eee"%3E%3Crect width="16" height="9" /%3E%3C/svg%3E';

    const fetchList = async (pageNum = 1, append = false) => {
      loading.value = true;
      try {
        const res = await getWatchHistoryList({ page: pageNum });
        if (res.code === 1) {
          const data = res.data;
          const items = data.list || data.data || [];
          if (append) {
            list.value = [...list.value, ...items];
          } else {
            list.value = items;
          }
          hasMore.value = items.length >= (data.per_page || 10);
          page.value = pageNum;
        }
      } catch (e) {
        console.error('获取观看历史失败', e);
      } finally {
        loading.value = false;
      }
    };

    const loadMore = () => {
      if (!hasMore.value || loading.value) return;
      fetchList(page.value + 1, true);
    };

    const deleteItem = async (id) => {
      try {
        const res = await request.post('/api/user/deleteWatchHistory', {
          id: id
        });
        if (res.code === 1) {
          list.value = list.value.filter(item => item.id !== id);
          if (list.value.length === 0) {
            page.value = 1;
            hasMore.value = true;
          }
        } else {
          alert(res.msg || '删除失败');
        }
      } catch (e) {
        alert(e?.message || '删除失败，请重试');
      }
    };

    const confirmClear = () => {
      if (confirm('确定要清空所有观看历史吗？')) {
        clearAll();
      }
    };

    const clearAll = async () => {
      try {
        const res = await clearWatchHistory();
        if (res.code === 1) {
          list.value = [];
          page.value = 1;
          hasMore.value = true;
        } else {
          alert(res.msg || '清空失败');
        }
      } catch (e) {
        alert(e?.message || '清空失败，请重试');
      }
    };

    const formatTime = (timestamp) => {
      if (!timestamp) return '';
      if (typeof timestamp === 'string' && !isNaN(Date.parse(timestamp))) {
        const d = new Date(timestamp);
        return d.toLocaleString('zh-CN', {
          month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false
        });
      }
      const num = Number(timestamp);
      if (isNaN(num) || num <= 0) return '';
      const date = new Date(num * 1000);
      if (isNaN(date.getTime())) return '';
      return date.toLocaleString('zh-CN', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: false
      });
    };

    const goVideo = (videoId) => {
      window.location.hash = '#/video/' + videoId;
    };

    onMounted(() => {
      fetchList();
    });

    return {
      list,
      loading,
      hasMore,
      defaultCover,
      loadMore,
      deleteItem,
      confirmClear,
      formatTime,
      goVideo
    };
  }
};
</script>

<style scoped>
.history-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, #f6f7f9 0%, #f0f2f5 100%);
  padding: 40px 0;
}
.history-page.embedded {
  min-height: auto;
  padding: 0;
  background: transparent;
}
.history-page.embedded .container {
  max-width: none;
  margin: 0;
  padding: 0;
}
.container {
  flex: 1;
  max-width: 720px;
  margin: 0 auto;
  padding: 0 20px;
  width: 100%;
  box-sizing: border-box;
}
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
}
.page-title {
  font-size: 24px;
  color: #1a1a2e;
  font-weight: 700;
  margin: 0;
  position: relative;
  padding-left: 16px;
}
.page-title::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 24px;
  background: linear-gradient(180deg, #ff6a00, #ff9600);
  border-radius: 2px;
}
.clear-btn {
  padding: 8px 20px;
  background: #fff;
  border: 1px solid #ef4444;
  color: #ef4444;
  border-radius: 18px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.3s;
  font-weight: 500;
}
.clear-btn:hover {
  background: #ef4444;
  color: #fff;
  box-shadow: 0 2px 8px rgba(239,68,68,0.3);
  transform: translateY(-1px);
}

/* 空状态 */
.empty-tip {
  text-align: center;
  padding: 80px 20px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.04);
}
.empty-icon {
  margin-bottom: 16px;
  color: #d1d5db;
}
.empty-text {
  font-size: 16px;
  color: #666;
  margin: 0 0 6px;
  font-weight: 500;
}
.empty-sub {
  font-size: 13px;
  color: #bbb;
  margin: 0;
}

/* 列表 */
.history-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.history-card {
  display: flex;
  align-items: center;
  background: #fff;
  border-radius: 14px;
  padding: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  border: 1px solid rgba(0,0,0,0.02);
}
.history-card:hover {
  box-shadow: 0 8px 24px rgba(0,0,0,0.08);
  transform: translateY(-2px);
  border-color: rgba(255,150,0,0.1);
}
.history-thumb {
  position: relative;
  width: 140px;
  height: 79px;
  border-radius: 10px;
  overflow: hidden;
  flex-shrink: 0;
  margin-right: 16px;
  background: #f0f0f0;
}
.history-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s ease;
}
.history-card:hover .history-thumb img {
  transform: scale(1.08);
}
.thumb-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s;
}
.history-card:hover .thumb-overlay {
  opacity: 1;
}
.history-duration {
  position: absolute;
  bottom: 5px;
  right: 5px;
  background: rgba(0,0,0,0.8);
  color: #fff;
  font-size: 11px;
  padding: 2px 7px;
  border-radius: 4px;
  font-weight: 500;
}
.history-info {
  flex: 1;
  min-width: 0;
}
.history-title {
  font-size: 15px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0 0 8px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;
}
.history-meta {
  font-size: 12px;
  color: #999;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.watch-time {
  color: #ff9600;
  font-weight: 500;
}
.history-date {
  color: #bbb;
}
.delete-btn {
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: 1px solid #e2e8f0;
  color: #ccc;
  border-radius: 50%;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.3s;
  margin-left: 12px;
}
.delete-btn:hover {
  border-color: #ef4444;
  color: #ef4444;
  background: rgba(239,68,68,0.06);
  transform: scale(1.1);
}

.load-more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 18px;
  color: #ff9600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s;
  font-weight: 500;
}
.load-more:hover {
  color: #ff7d00;
  gap: 10px;
}
.load-end {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 18px;
}
.load-end-line {
  width: 40px;
  height: 1px;
  background: #e2e8f0;
}
.load-end-text {
  color: #ccc;
  font-size: 12px;
}

.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 80px 20px;
  color: #999;
  font-size: 14px;
}
.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e2e8f0;
  border-top: 3px solid #ff9600;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>